// resources/js/Pages/HallOfFame/components/ScriptRow.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ScoreCell from './ScoreCell';
import ScriptRowExpanded from './ScriptRowExpanded';
import GenreBarcode from './GenreBarcode';
import { parsePCR, parseAvgScore } from '../utils/pcr';
import { getHighlightClass } from '../utils/percentiles';

export default function ScriptRow({ script, onVideoClick, onGenreClick, onContactWriterClick, allScripts, auth }) {
    const [isExpanded, setIsExpanded] = useState(false);
    // Heavy per-script blobs (rollup tooltips + emotional analysis) are not in the
    // initial payload — fetch them on first expand, like the larger image loads on open.
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    // Awards tooltip anchor ({x, y} viewport coords) — null when hidden
    const [awardsTip, setAwardsTip] = useState(null);

    const handleToggle = () => {
        const next = !isExpanded;
        setIsExpanded(next);
        if (next && !detail && !detailLoading && script.hash) {
            setDetailLoading(true);
            fetch(`/best/script-detail?hash=${encodeURIComponent(script.hash)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => { if (data && !data.error) setDetail(data); })
                .catch(() => {})
                .finally(() => setDetailLoading(false));
        }
    };

    // Safety check: If script or percentiles are missing, don't render or render safely
    if (!script) return null;
    
    // Default to empty object if percentiles is missing to prevent crashes
    const percentiles = script.percentiles || {};
    
    const pcr = parsePCR(script.pcr_list, script.isVault);
    // Vault-only column. Older analyses have no per-reader score, so this is
    // null for them and the cell renders blank (see parseAvgScore).
    const avgScore = parseAvgScore(script.pcr_list);

    return (
        <>
            <tr className="tr-data">
                {/* Star/Laurel Column - hidden for vault */}
                {!script.isVault && (
                <td>
                    {script.criteria_name && (
                        <div className="d-flex flex-column justify-content-start align-items-center best_role">
                            <em><img src="/images/icons/star_icon.png" alt="" width="33" height="31" /></em>
                            <div>{script.criteria_name}</div>
                        </div>
                    )}
                    {/* Awards laurel — custom hover tooltip shows the writer's self-reported awards.
                        position:fixed (from the icon's bounding rect) so the table's overflow
                        containers can't clip it; instant show, styled to match the site gradient. */}
                    {!script.criteria_name && script.awards && (
                        <div
                            className="d-flex flex-column justify-content-start align-items-center best_role"
                            style={{ cursor: 'help' }}
                            onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setAwardsTip({ x: r.right + 10, y: r.top + r.height / 2 });
                            }}
                            onMouseLeave={() => setAwardsTip(null)}
                        >
                            <em><img src="/images/icons/laurel_gold.png" alt="Awards" width="44" height="24" /></em>
                            {/* Portal to <body>: .custom-table's backdrop-filter creates a containing
                                block that hijacks position:fixed descendants, so the tooltip must
                                render outside the table to position against the real viewport. */}
                            {awardsTip && createPortal(
                                <div
                                    style={{
                                        position: 'fixed',
                                        left: awardsTip.x,
                                        top: awardsTip.y,
                                        transform: 'translateY(-50%)',
                                        background: 'linear-gradient(135deg, #083A54 0%, #0A6A64 100%)',
                                        color: '#fff',
                                        padding: '10px 14px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                                        zIndex: 2000,
                                        maxWidth: '300px',
                                        textAlign: 'left',
                                        pointerEvents: 'none',
                                        whiteSpace: 'pre-line',
                                        fontSize: '0.85rem',
                                        lineHeight: 1.45,
                                    }}
                                >
                                    {script.awards}
                                    <div
                                        style={{
                                            marginTop: '6px',
                                            fontSize: '0.72rem',
                                            fontStyle: 'italic',
                                            color: 'rgba(255,255,255,0.65)',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        Self-reported by the writer
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    )}
                </td>
                )}
                
                {/* Video Button */}
                <td>
                    {script.video_url && (
                        <button 
                            className="btn btn-video-play"
                            onClick={() => onVideoClick(script)}
                            title="Watch Video"
                        >
                            <img src="/images/icons/play_icon.svg" alt="Play" width="24" height="24" />
                        </button>
                    )}
                </td>
                
                {/* Analysis Button */}
                <td>
                    <a
                        href={script.isVault && script.hash
                            ? `/disp?movieid=${script.hash}`
                            : `/view_analysis?submitted_id=${script.id}`}
                        target="_blank"
                        rel="nofollow"
                        title="View Analysis"
                        className={script.isVault ? 'btn btn-sm btn-dark vault-action-btn' : ''}
                    >
                        {script.isVault ? (
                            <>Analysis <i className="fa fa-external-link" style={{ fontSize: '10px' }}></i></>
                        ) : (
                            <i className="fa fa-bar-chart text-success" style={{ fontSize: '20px' }}></i>
                        )}
                    </a>
                </td>

                {/* Read Script Button */}
                <td>
                    <a
                        href={script.isVault && script.hash
                            ? `/view_script?movieid=${script.hash}`
                            : `/view_script?submitted_id=${script.id}`}
                        target="_blank"
                        rel="nofollow"
                        title="Read Script"
                    >
                        <i className="fa fa-book text-primary" style={{ fontSize: '20px' }}></i>
                    </a>
                </td>
                
                {/* Title with Expand Button */}
                <td className="text-start dark-blue-bg">
                    <div
                        className={`toggle ${isExpanded ? 'up' : ''} d-inline-flex justify-content-start`}
                        onClick={handleToggle}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="d-flex justify-content-start align-items-center screenplay_title">
                            <span className="ms-2">{script.title}</span>
                        </div>
                        <button className="collapse_bar">
                            <img src="/images/icons/arrow_rht_icon.svg" alt="" width="19" height="14" />
                        </button>
                    </div>
                </td>
            
            {/* Genre - Genre Barcode with Modal */}
            <td className="text-start" style={{ minWidth: '150px' }}>
                <GenreBarcode 
                    script={script}
                    onClick={onGenreClick}
                    showLabelsOnHover={false}
                    showExpandIcon={true}
                />
            </td>
            
            {/* PCR */}
            <td 
                data-order={pcr.ratingNumber}
                title={pcr.tooltip}
                dangerouslySetInnerHTML={{ __html: pcr.display }}
                style={{ fontWeight: 'bold' }}
            />

            {/* Avg Score (0–10) — Script Vault only. Blank when the analysis
                predates per-reader scores and carries only P/C/R verdicts. */}
            {script.isVault && (
                <td
                    className="text-center pcr_score_col"
                    data-order={avgScore.value ?? ''}
                    title={avgScore.tooltip}
                    style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                >
                    {avgScore.value !== null ? avgScore.value.toFixed(1) : ''}
                </td>
            )}

            {/* ========== OVERALL SECTION ========== */}
            {/* Overall Average - VISIBLE IN GROUPED */}
            <td 
                className={`overall_average ${getHighlightClass(percentiles.overall_avg)}`}
                title={percentiles.overall_avg !== null && percentiles.overall_avg !== undefined
                    ? `${percentiles.overall_avg}% (${((script.scores.sl_overall_grade + script.scores.rating) / 2).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.overall_avg !== null && percentiles.overall_avg !== undefined ? `${percentiles.overall_avg}%` : 'N/A'}
            </td>
            
            {/* Overall Script - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color"
                percentile={percentiles.sl_overall_grade}
                rawScore={script.scores.sl_overall_grade}
            />
            
            {/* Overall Scene - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.rating}
                rawScore={script.scores.rating}
            />
            
            {/* ========== STRUCTURE SECTION ========== */}
            {/* Structure Average - VISIBLE IN GROUPED */}
            <td 
                className={`sp_average group_border ${getHighlightClass(percentiles.structure_avg)}`}
                colSpan="3"
                title={percentiles.structure_avg !== null && percentiles.structure_avg !== undefined
                    ? `${percentiles.structure_avg}% (${((script.scores.sl_structure_grade + script.scores.plotrating + script.scores.story_forward) / 3).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.structure_avg !== null && percentiles.structure_avg !== undefined ? `${percentiles.structure_avg}%` : 'N/A'}
            </td>
            
            {/* Structure Script - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color group_border"
                percentile={percentiles.sl_structure_grade} 
                rawScore={script.scores.sl_structure_grade}
            />
            
            {/* Plot Scene - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.plotrating} 
                rawScore={script.scores.plotrating} 
            />
            
            {/* Story Forward - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.story_forward} 
                rawScore={script.scores.story_forward} 
            />
            
            {/* ========== CHARACTERS SECTION ========== */}
            {/* Characters Average - VISIBLE IN GROUPED */}
            <td 
                className={`group_border ${getHighlightClass(percentiles.characters_avg)}`}
                colSpan="3"
                title={percentiles.characters_avg !== null && percentiles.characters_avg !== undefined
                    ? `${percentiles.characters_avg}% (${((script.scores.sl_characters_grade + script.scores.charactersrating + script.scores.character_changes) / 3).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.characters_avg !== null && percentiles.characters_avg !== undefined ? `${percentiles.characters_avg}%` : 'N/A'}
            </td>
            
            {/* Characters Script - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color group_border"
                percentile={percentiles.sl_characters_grade} 
                rawScore={script.scores.sl_characters_grade}
            />
            
            {/* Characters Scene - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.charactersrating} 
                rawScore={script.scores.charactersrating} 
            />
            
            {/* Character Changes - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.character_changes} 
                rawScore={script.scores.character_changes} 
            />
            
            {/* ========== CONFLICT SECTION ========== */}
            {/* Conflict Average - VISIBLE IN GROUPED */}
            <td 
                className={`script_color group_border ${getHighlightClass(percentiles.conflict_avg)}`}
                colSpan="3"
                title={percentiles.conflict_avg !== null && percentiles.conflict_avg !== undefined
                    ? `${percentiles.conflict_avg}% (${((script.scores.sl_conflict_grade + script.scores.conflict_level + script.scores.stakes) / 3).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.conflict_avg !== null && percentiles.conflict_avg !== undefined ? `${percentiles.conflict_avg}%` : 'N/A'}
            </td>
            
            {/* Conflict Script - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color group_border"
                percentile={percentiles.sl_conflict_grade} 
                rawScore={script.scores.sl_conflict_grade}
            />
            
            {/* Conflict Scene - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.conflict_level} 
                rawScore={script.scores.conflict_level} 
            />
            
            {/* Stakes - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.stakes} 
                rawScore={script.scores.stakes} 
            />
            
            {/* ========== EMOTIONAL SECTION ========== */}
            {/* Emotional Average - VISIBLE IN GROUPED */}
            <td 
                className={`group_border ${getHighlightClass(percentiles.emotional_avg)}`}
                colSpan="3"
                title={percentiles.emotional_avg !== null && percentiles.emotional_avg !== undefined
                    ? `${percentiles.emotional_avg}% (${((script.scores.sl_ei_grade + script.scores.emotional_impact + script.scores.sl_theme_grade) / 3).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.emotional_avg !== null && percentiles.emotional_avg !== undefined ? `${percentiles.emotional_avg}%` : 'N/A'}
            </td>
            
            {/* Emotional Script - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color group_border"
                percentile={percentiles.sl_ei_grade} 
                rawScore={script.scores.sl_ei_grade}
            />
            
            {/* Emotional Scene - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.emotional_impact} 
                rawScore={script.scores.emotional_impact} 
            />
            
            {/* Theme - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color"
                percentile={percentiles.sl_theme_grade} 
                rawScore={script.scores.sl_theme_grade}
            />
            
            {/* ========== DIALOGUE/PREMISE SECTION ========== */}
            {/* Dialogue/Premise Average - VISIBLE IN GROUPED */}
            <td 
                className={`script_color group_border ${getHighlightClass(percentiles.dialogue_premise_avg)}`}
                colSpan="5"
                title={percentiles.dialogue_premise_avg !== null && percentiles.dialogue_premise_avg !== undefined
                    ? `${percentiles.dialogue_premise_avg}% (${((script.scores.dialoguerating + script.scores.sl_vi_grade + script.scores.sl_originality_grade + script.scores.conceptrating + script.scores.sl_premise_grade) / 5).toFixed(1)})` 
                    : 'N/A'}
                style={{ fontWeight: 'bold' }}
            >
                {percentiles.dialogue_premise_avg !== null && percentiles.dialogue_premise_avg !== undefined ? `${percentiles.dialogue_premise_avg}%` : 'N/A'}
            </td>
            
            {/* Dialogue - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group group_border"
                percentile={percentiles.dialoguerating} 
                rawScore={script.scores.dialoguerating} 
            />
            
            {/* Visual Imagery - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color"
                percentile={percentiles.sl_vi_grade} 
                rawScore={script.scores.sl_vi_grade}
            />
            
            {/* Originality - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color"
                percentile={percentiles.sl_originality_grade} 
                rawScore={script.scores.sl_originality_grade}
            />
            
            {/* Concept - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group"
                percentile={percentiles.conceptrating} 
                rawScore={script.scores.conceptrating} 
            />
            
            {/* Premise - HIDDEN IN GROUPED */}
            <ScoreCell 
                className="colspan_group script_color"
                percentile={percentiles.sl_premise_grade} 
                rawScore={script.scores.sl_premise_grade}
            />
            </tr>
            
            {/* Expanded Row */}
            {isExpanded && (
                <ScriptRowExpanded
                    script={detail ? { ...script, ...detail } : script}
                    allScripts={allScripts}
                    detailLoading={detailLoading}
                    onContactWriterClick={() => onContactWriterClick(script)}
                    auth={auth}
                />
            )}
        </>
    );
}
